import SendIcon from '@/components/icons/SendIcon';
import UserGroupIcon from '@/components/icons/UserGroupIcon';
import { PoweredByLogo } from '@/components/pages/public/PoweredByLogo';
import { Separator } from '@/components/ui/separator';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { cn, getFontFamily } from '@/lib/utils';
import { Circle, CircleX, WifiHigh } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function PreviewLive({
  visualIdentity,
  accentColor: _accentColor,
  backgroundColor,
  contrastColor,
  theme,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  accentColor: string;
  backgroundColor: string;
  contrastColor: string;
  theme?: 'LIGHT' | 'DARK';
}) {
  const { t } = useTranslation('webinars');
  const { t: tCommon } = useTranslation('common');
  const currentTheme = theme || visualIdentity?.theme || 'LIGHT';
  const isDark = currentTheme === 'DARK';
  const borderClass = isDark ? 'border-input-inverse' : 'border-input';
  const separatorBgClass = isDark ? 'bg-input-inverse' : 'bg-input';
  const bgClass = isDark ? 'bg-[var(--color-dark)]' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-black';
  const fontFamilyStyle = { fontFamily: getFontFamily(visualIdentity?.fontFamily) };

  const previewMessages = [
    {
      id: 1,
      name: 'Patrick',
      content: t('visualIdentity.livePreview.messages.great'),
      reactions: { '👍': 2, '❤️': 1 },
    },
    {
      id: 2,
      name: 'Marie',
      content: t('visualIdentity.livePreview.messages.thanks'),
      reactions: { '🔥': 3 },
    },
    {
      id: 3,
      name: 'Jean',
      content: t('visualIdentity.livePreview.messages.greatContent'),
      reactions: {},
    },
    {
      id: 4,
      name: 'Sophie',
      content: t('visualIdentity.livePreview.messages.question'),
      reactions: { '💡': 1, '👍': 1 },
    },
    {
      id: 5,
      name: 'Thomas',
      content: t('visualIdentity.livePreview.messages.excellent'),
      reactions: { '❤️': 2, '🔥': 1 },
    },
    {
      id: 6,
      name: 'Lucas',
      content: t('visualIdentity.livePreview.messages.thanks'),
      reactions: { '🔥': 3 },
    },
  ];

  return (
    <div
      className=" overflow-hidden w-full rounded-[20px] relative flex gap-4 items-start justify-between"
      style={{
        backgroundColor: backgroundColor,
      }}
    >
      {/* left */}
      <div className="w-2/3 flex flex-col gap-6 py-4 pl-4">
        <div className="flex flex-col gap-2">
          <img src="/assets/presenter.png" alt="presenter" />

          <div
            className={cn(
              'border shadow-card rounded-[20px] px-2 py-1 space-x-1 flex items-center w-fit',
              borderClass,
            )}
          >
            {['👍', '👏', '❤️', '👋', '🥳'].flatMap((emoji, index, array) => [
              <button
                key={emoji}
                className="text-[12px] cursor-pointer hover:scale-110 transition-transform"
              >
                {emoji}
              </button>,
              ...(index < array.length - 1
                ? [
                    <Separator
                      key={`separator-${emoji}`}
                      orientation="vertical"
                      className={cn('h-full', separatorBgClass)}
                    />,
                  ]
                : []),
            ])}
          </div>
        </div>

        <div className="w-full flex items-center justify-between ">
          <PoweredByLogo
            theme={currentTheme as 'LIGHT' | 'DARK'}
            classNameText="text-[10px]"
            classNameLogo="w-10"
          />
          <div
            className={cn(
              'h-6 p-1 px-2 shadow-card rounded-full flex items-center justify-center gap-0.5 border',
              bgClass,
              borderClass,
            )}
          >
            <div className="flex items-center gap-0.5">
              <p
                className={cn('flex items-center justify-center gap-2 text-[8px]', textClass)}
                style={fontFamilyStyle}
              >
                <Circle className="w-2 h-2" fill="red" stroke="red" />
                {t('visualIdentity.livePreview.recording', { time: '10.52' })}
              </p>
            </div>

            <Separator orientation="vertical" className={cn('h-full', separatorBgClass)} />

            <div className="flex items-center gap-2">
              <UserGroupIcon size={12} />
              <p className={textClass + ' text-[8px]'} style={fontFamilyStyle}>
                10
              </p>
            </div>

            <Separator orientation="vertical" className={cn('h-full', separatorBgClass)} />

            <WifiHigh size={12} color="green" />

            <div className="flex items-center gap-0.5 cursor-pointer">
              <CircleX size={12} color="red" />
              <p className="text-red-500 text-[8px]" style={fontFamilyStyle}>
                {tCommon('liveSettings.closeSessionButton')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* right */}
      <div
        style={{
          backgroundColor:
            currentTheme === 'LIGHT' ? 'rgba(31, 31, 31, 0.08)' : 'rgba(255, 255, 255, 0.2)',
        }}
        className="w-1/3 rounded-r-[20px] self-stretch flex flex-col"
      >
        {/* messages container - prend tout l'espace et pousse vers le bas */}
        <div className="flex-1 flex flex-col justify-end gap-2 pt-2 overflow-y-auto">
          {[...previewMessages].reverse().map((message) => (
            <div
              key={message.id}
              className="flex flex-col relative p-2 mx-2 rounded-t-lg rounded-br-lg "
              style={{
                backgroundColor: visualIdentity?.defaultIdentity
                  ? currentTheme === 'LIGHT'
                    ? '#FFFFFF'
                    : '#1F1F1F'
                  : backgroundColor,
              }}
            >
              <div className="flex items-center gap-1">
                <span
                  className={`text-[8px] font-normal ${
                    currentTheme === 'LIGHT' ? 'text-secondary' : 'text-white'
                  }`}
                  style={fontFamilyStyle}
                >
                  {message.name}
                </span>
              </div>

              {message.reactions && Object.keys(message.reactions).length > 0 && (
                <div className="absolute top-1 right-1">
                  <div className="flex items-center gap-1">
                    {Object.entries(message.reactions).map(([emoji, count]) =>
                      count > 0 ? (
                        <span
                          key={emoji}
                          className="rounded-full py-0.5 px-1.5 text-[8px]"
                          style={{
                            ...fontFamilyStyle,
                            color: currentTheme === 'LIGHT' ? '#000000' : '#FFFFFF',
                            backgroundColor:
                              currentTheme === 'LIGHT'
                                ? 'rgba(31, 31, 31, 0.03)'
                                : 'rgba(255, 255, 255, 0.1)',
                          }}
                        >
                          {emoji} {count}
                        </span>
                      ) : null,
                    )}
                  </div>
                </div>
              )}

              <p className={cn(textClass, 'text-[10px]')} style={fontFamilyStyle}>
                {message.content}
              </p>
            </div>
          ))}
        </div>
        {/* input */}
        <div className="w-full shrink-0 pt-2">
          <div
            className={cn(
              'w-full flex items-center justify-between px-2 rounded-t-[20px]',
              !visualIdentity?.defaultIdentity && 'border-t border-l border-r',
            )}
            style={{
              backgroundColor: visualIdentity?.defaultIdentity
                ? currentTheme === 'LIGHT'
                  ? '#FFFFFF'
                  : '#0C0E21'
                : currentTheme === 'DARK'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(31, 31, 31, 0.08)',
              ...(!visualIdentity?.defaultIdentity && {
                borderColor: contrastColor,
              }),
            }}
          >
            <textarea
              placeholder={t('live.chat.writeHere')}
              readOnly
              className={cn(
                'w-full h-8 py-2 resize-none focus:outline-none placeholder:text-[10px] ',
                currentTheme === 'LIGHT' ? 'text-tertiary' : 'text-tertiary-inverse',
              )}
              style={fontFamilyStyle}
            />
            <button className="cursor-pointer" aria-label={'send'}>
              <span style={{ color: contrastColor }}>
                <SendIcon size={18} />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
